import { capture } from '@/handlers/error';
import { useRecorder } from '@/hooks/useRecorder/useRecorder';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    onUploadAudio?: (file: File) => Promise<void>;
};

export const AudioRecorderView: React.FC<Props> = ({ onUploadAudio }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { isRecording, permission, audio, getMicrophonePermission, startRecording, stopRecording } = useRecorder();
    const [uploading, setUploading] = useState(false);

    const onStartRecordingHandler = async () => {
        try {
            if (permission) {
                startRecording();
            } else {
                await getMicrophonePermission();
                startRecording();
            }
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            capture(error);
        }
    };

    useEffect(() => {
        if (!audio) return;

        setUploading(true);
        onUploadAudio?.(audio).finally(() => setUploading(false));
    }, [audio, onUploadAudio]);

    return (
        <Box
            sx={(theme) => ({
                p: 1,
                flex: 1,
                border: 1,
                borderRadius: 0.5,
                borderStyle: 'dashed',
                borderColor: (theme.vars || theme).palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                backgroundColor: (theme.vars || theme).palette.grey[100],
                ...theme.applyStyles('dark', {
                    backgroundColor: (theme.vars || theme).palette.grey[700],
                }),
            })}
        >
            {!isRecording && !uploading ? (
                <Stack spacing={1.5}>
                    <Typography sx={{ textAlign: 'center' }}>{t('slide.components.assets.audio.clickToRecord')}</Typography>
                    <Button
                        variant='outlined'
                        startIcon={<GraphicEqIcon />}
                        sx={{
                            width: 'fit-content',
                            alignSelf: 'center',
                        }}
                        onClick={onStartRecordingHandler}
                    >
                        {t('slide.components.assets.audio.record')}
                    </Button>
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    <Typography sx={{ textAlign: 'center' }}>{t('slide.components.assets.audio.recordingNow')}</Typography>
                    <Typography sx={{ textAlign: 'center' }}>{t('slide.components.assets.audio.clickToStopRecording')}</Typography>
                    <Button
                        variant='outlined'
                        startIcon={<StopIcon />}
                        sx={{ width: 'fit-content', alignSelf: 'center' }}
                        onClick={stopRecording}
                        loading={uploading}
                        loadingIndicator={<CircularProgress size={16} />}
                    >
                        {t('slide.components.assets.audio.stopRecord')}
                    </Button>
                </Stack>
            )}
        </Box>
    );
};
